<template>
    <div>
        <race-number v-if="! edit" :number="entry.race_number" :entry-id="entry.id">
            <slot name="race-number" :number="entry.race_number"></slot>
        </race-number>
        <div class="race-number-form" v-else>
            <select v-if="showSelectBox"
                    v-model="newRaceNumber"
                    class="form-control"
                    @keyup.enter="update"
                    v-focus>
                <option v-for="number in availableNumbers" :value="number" :disabled="! isInt(number)">
                    {{ number }}
                </option>
            </select>
            <input v-else class="form-control race-number"
                   v-model="newRaceNumber"
                   required
                   @keyup.enter="update"
                   v-focus-select>
            <button class="btn btn-primary" :class="{ 'btn-lg': large }" @click.prevent="update">Save</button>
            <a href="#" class="btn btn-default pull-right" :class="{ 'btn-lg': large }" @click.prevent="cancelEdit">Cancel</a>
            <div v-if="!manualEntryOnly">
                <small><a href="#" @click.prevent="showSelectBox = !showSelectBox">
                    {{ showSelectBox ? 'Enter another value' : 'Choose value' }}
                </a></small>
            </div>
        </div>
        <slot name="edit-button"
              v-if="! edit && entry.is_approved"
              :toggle-edit="toggleEdit"
              :assign-next-number="assignNextNumber"
              :loading-race-numbers="loadingRaceNumbers">
            <a href="#" class="btn btn-default pull-right" @click.prevent="assignNextNumber">
                <template v-if="!loadingRaceNumbers">Assign new event number</template>
                <template v-else><i class="fa fa-circle-o-notch fa-spin"></i> Loading...</template>
            </a>
        </slot>
    </div>
</template>

<script>
  import RaceNumber from "./RaceNumber";
  import { mapMutations } from 'vuex';
  import { setSearchQuery } from '../../store/mutations';

  const isInt = (number) => {
    return Number.isInteger(number);
  };

  const assignNumberAlert = (raceNumber, availableCount) => {
    const buttons = {
      success: 'I have found the number and given it to the participant',
    };

    if (availableCount > 1) {
      buttons.cancel = 'I couldn\'t find the number, assign a different one';
      buttons.edit = 'Manually choose a new number';
    } else {
      buttons.manualEntry = 'Manually enter a new number';
    }

    return window.HelpdeskAlert
      .confirm({
        title: `Please get the number ${raceNumber} and hand it to the participant`,
        text: '',
        icon: 'success',
        buttons,
        closeOnClickOutside: false,
        closeOnEsc: false,
      });
  };

  const noNumbersAvailableAlert = (numberSetNames) => {
    const text = numberSetNames
      ? `All numbers from "${numberSetNames}" have been allocated.`
      : 'There are no race numbers available to allocate.';

    return window.HelpdeskAlert
      .confirm({
        title: 'No race numbers available',
        text,
        icon: 'warning',
        buttons: {
          close: 'Close',
          manualEntry: 'Manually enter a new number',
        },
        closeOnClickOutside: true,
        closeOnEsc: true,
      });
  };

  export default {
    data() {
      return {
        edit: false,
        newRaceNumber: null,
        availableNumbers: null,
        showSelectBox: true,
        loadingRaceNumbers: false,
        manualEntryOnly: false,
      };
    },

    components: {
      RaceNumber,
    },

    props: ['entry', 'large'],

    methods: {
      ...mapMutations(['setSearchQuery']),

      isInt,

      assignNextNumber() {
        window.axios.post(route('entry.assign_new_race_number', this.entry.id).toString())
          .then(({ data }) => {
            if (! data.race_number) {
              noNumbersAvailableAlert(data.number_set_names)
                .then(result => {
                  if (result === 'manualEntry') {
                    this.enterManualEntry();
                  }
                });
              return;
            }

            this.updateNumberInStore(data.race_number);

            assignNumberAlert(data.race_number, data.available_count)
              .then(result => {
                switch (result) {
                  case 'success':
                    // We've already updated the number in the store and database so just continue
                    break;
                  case 'edit':
                    this.toggleEdit();
                    break;
                  case 'manualEntry':
                    this.enterManualEntry();
                    break;
                  default:
                    this.assignNextNumber();
                }
              });
          })
          .catch(error => {
            console.error(error);
            window.HelpdeskAlert.alertError({ text: 'There was an error assigning a race number.' });
          });
      },

      enterManualEntry() {
        this.newRaceNumber = this.entry.race_number;
        this.showSelectBox = false;
        this.manualEntryOnly = true;
        this.edit = true;
      },

      cancelEdit() {
        this.edit = false;
        this.manualEntryOnly = false;
        this.showSelectBox = true;
      },

      toggleEdit() {
        let finishEditing = () => {
          this.edit = ! this.edit;
          this.loadingRaceNumbers = false;
        };

        this.newRaceNumber = this.entry.race_number;
        this.showSelectBox = true;

        if (! this.availableNumbers && ! this.edit) {
          this.loadingRaceNumbers = true;
          window.axios.get(route('entry.available_race_numbers', this.entry.id).toString())
            .then(({ data }) => {
              // Remove the current race number from list and prepend it so it appears first
              let availableNumbers = data.filter(number => number !== this.entry.race_number);
              let prependedValues = ['Select event number', '', this.entry.race_number, ''];
              this.availableNumbers = prependedValues.concat(availableNumbers);
              finishEditing();
            })
            .catch(error => {
              window.HelpdeskAlert.alertError({ text: 'There was an error.' });
              console.error(error);
              finishEditing();
            });
        } else {
          return this.edit = ! this.edit;
        }
      },

      update() {
        window.axios.patch(route('entry.update', this.entry.id).toString(), { race_number: this.newRaceNumber })
          .then(() => {
            this.edit = false;
            this.updateNumberInStore(this.newRaceNumber);
          })
          .catch(error => {
            console.log(error);
            window.HelpdeskAlert.alertError({ text: 'There was an error.' });
          });
      },

      updateNumberInStore(raceNumber) {
        this.$store.commit('updateRaceNumber', {
          entryId: this.entry.id,
          newNumber: raceNumber,
        });
        this.$store.commit('setSearchCache', null);
      },
    },
  };
</script>

<style scoped>
    .race-number-form {
        display: inline;
    }

    input.race-number, select {
        max-width: 150px;
        display: inline;
    }
</style>
